import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import states from '../../../../components/common/states'; // adjust path as needed
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SaleBrokerForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    commission_type: 'PERCENT',
    commission_value: '',
    contact: '',
    address: '',
    gst_no: '',
    state: '',
    state_code: '',
    aadhar_no: '',
    pan_no: '',
    email: '',
    bank_name: '',
    bank_address: '',
    account_no: '',
    ifsc_code: ''
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    commission_type: 'PERCENT',
    commission_value: '',
    contact: '',
    address: '',
    gst_no: '',
    state: '',
    state_code: '',
    aadhar_no: '',
    pan_no: '',
    email: '',
    bank_name: '',
    bank_address: '',
    account_no: '',
    ifsc_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const navigate = useNavigate();

  // Fetch sale broker data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching sale broker with ID:', id);
      const fetchSaleBroker = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/sale-broker-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch sale broker');
          }
          const result = await res.json();
          console.log('Fetched sale broker data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            const newFormData = {
              name: result.data.name ?? '',
              commission_type: result.data.commission_type ?? 'PERCENT',
              commission_value: result.data.commission_value ?? '',
              contact: result.data.contact ?? '',
              address: result.data.address ?? '',
              gst_no: result.data.gst_no ?? '',
              state: result.data.state ?? '',
              state_code: result.data.state_code ?? '',
              aadhar_no: result.data.aadhar_no ?? '',
              pan_no: result.data.pan_no ?? '',
              email: result.data.email ?? '',
              bank_name: result.data.bank_name ?? '',
              bank_address: result.data.bank_address ?? '',
              account_no: result.data.account_no ?? '',
              ifsc_code: result.data.ifsc_code ?? ''
            };
            console.log('Setting new form data:', newFormData);
            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original values for read-only check
          } else {
            toast.error('Sale Broker not found');
            navigate('/broker');
          }
        } catch (error) {
          console.error('Error fetching sale broker:', error);
          toast.error('Failed to load sale broker data');
          navigate('/broker');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchSaleBroker();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const FIELD_LIMITS = {
    name: 100,
    commission_type: 10,
    commission_value: 10, // decimal(10,2) - input type number, so not needed for maxLength
    contact: 20,
    address: 255,
    gst_no: 20,
    state: 100,
    state_code: 10,
    aadhar_no: 20,
    pan_no: 20,
    email: 100,
    bank_name: 100,
    bank_address: 255,
    account_no: 30,
    ifsc_code: 20,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = ['name', 'commission_type', 'commission_value', 'contact', 'address', 'gst_no', 'state', 'account_no', 'email', 'bank_name'];
    if (requiredFields.includes(name) && (!value || value.trim() === '')) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if field is read-only (has existing value from database)
    const isReadOnly = (fieldName) => {
      // Only check if this is edit mode and the field had an original value
      if (!isEditMode) return false;
      
      // Get the original value from the database (stored separately)
      const originalValue = originalFormData[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    };
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'commission_value' ? value.replace(/[^0-9.]/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = ['name', 'commission_type', 'commission_value', 'contact', 'address', 'gst_no', 'state', 'account_no', 'email', 'bank_name'];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/sale-broker-put/${id}`
        : `${API_BASE_URL}/api/masters/sale-broker-post`;
      
      console.log('=== SUBMITTING SALE BROKER DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Sale Broker Updated Successfully!' : 'Broker Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            name: '',
            commission_type: 'PERCENT',
            commission_value: '',
            contact: '',
            address: '',
            gst_no: '',
            state: '',
            state_code: '',
            aadhar_no: '',
            pan_no: '',
            email: '',
            bank_name: '',
            bank_address: '',
            account_no: '',
            ifsc_code: ''
          });
        } else {
          navigate('/broker');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
            <div className="flex items-center gap-4">
              <h2 className="text-[15px] font-bold mb-0 text-left">{isEditMode ? 'Edit Sale Broker' : 'Sale Broker Entry'}</h2>
              {isEditMode && (
                <div className="text-[12px] text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

            {/* Existing Fields */}
            <div><label className="block text-[12px] font-medium mb-1">Broker Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.name} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">Commission Type</label><select name="commission_type" value={formData.commission_type} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.commission_type} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"><option value="PERCENT">PERCENT</option><option value="FLAT">FLAT</option></select></div>
            <div><label className="block text-[12px] font-medium mb-1">Commission Value</label><input type="number" name="commission_value" step="0.01" value={formData.commission_value} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">Contact Phone</label><input type="text" name="contact" value={formData.contact} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.contact} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>

            {/* New Fields */}
            <div><label className="block text-[12px] font-medium mb-1">Address</label><input type="text" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.address} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">GST No</label><input type="text" name="gst_no" value={formData.gst_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.gst_no} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
           <div> <label className="block text-[12px] font-medium mb-1">State</label>
              <select
                name="state"
                value={formData.state}
                onChange={(e) => {
                  const selectedState = e.target.value;
                  const matched = states.find(s => s.name === selectedState);
                  setFormData(prev => ({
                    ...prev,
                    state: selectedState,
                    state_code: matched ? matched.code : ''
                  }));
                }}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.state}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              >
                <option value="">-- Select State --</option>
                {states.map((s, i) => (
                  <option key={i} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">State Code</label>
              <input
                type="text"
                name="state_code"
                value={formData.state_code}
                readOnly
                maxLength={FIELD_LIMITS.state_code}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-gray-100 focus:outline-none focus:border-[#15803d] rounded-none"
              />
            </div>

            <div><label className="block text-[12px] font-medium mb-1">Aadhar No</label><input type="text" name="aadhar_no" value={formData.aadhar_no} onChange={handleChange} maxLength={FIELD_LIMITS.aadhar_no} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" /></div>
            <div><label className="block text-[12px] font-medium mb-1">PAN No</label><input type="text" name="pan_no" value={formData.pan_no} onChange={handleChange} maxLength={FIELD_LIMITS.pan_no} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" /></div>
            <div><label className="block text-[12px] font-medium mb-1">Email</label><input type="email" name="email" value={formData.email_id} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.email} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">Bank Name</label><input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.bank_name} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">Bank Address</label><input type="text" name="bank_address" value={formData.bank_address} onChange={handleChange} maxLength={FIELD_LIMITS.bank_address} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" /></div>
            <div><label className="block text-[12px] font-medium mb-1">Account No</label><input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.account_no} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" required /></div>
            <div><label className="block text-[12px] font-medium mb-1">IFSC Code</label><input type="text" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} maxLength={FIELD_LIMITS.ifsc_code} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" /></div>

          </div>
        </div>

        <div className="px-3 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Sale Broker' : 'Save Broker')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default SaleBrokerForm;
